import { Col, Form, notification, Row } from 'antd';
import { memo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import ButtonWrap from '@/components/ButtonWrap';
import ColorPicker from '@/components/ColorPicker';
import FormItemWrap from '@/components/FormItemWrap';
import InputMoney from '@/components/InputMoney';
import InputOnlyNumber from '@/components/InputOnlyNumber';
import InputWrap from '@/components/InputWrap';
import { pattern } from '@/constants/validate';
import { appRoutes } from '@/router/routes';
import RoomTypeService from '@/services/room-type.service';
import { handleMessageError } from '@/util/handleMessageError';
import { RequestFloorDTO } from '@/dto/responseDto/request-floor.dto';
import { RequestRoomTypeDTO } from '@/dto/responseDto/request-room-type.dto';
import './style.scss';

const FormData = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {
        control,
        reset,
        setValue,
        formState: { errors },
        clearErrors,
        handleSubmit,
    } = useForm();

    const { data: roomTypeItem } = useQuery(['room-type', id], () => RoomTypeService.detail(String(id)), {
        enabled: !!id,
        keepPreviousData: false,
        onError: (error: any) => {
            handleMessageError(error);
        },
    });

    const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(
        (e: RequestFloorDTO) => RoomTypeService.create(e),
        {
            onSuccess: (data: any) => {
                console.log('data', data);
                onGoList();
                queryClient.removeQueries('room-type');
                notification.success({
                    message: 'THÊM LOẠI PHÒNG THÀNH CÔNG',
                });
            },
            onError: (error: any) => {
                handleMessageError(error);
            },
        },
    );

    const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(
        (e: RequestFloorDTO) => RoomTypeService.update(String(id), e),
        {
            onSuccess: (data: any) => {
                onGoList();
                queryClient.removeQueries('room-type');
                notification.success({
                    message: 'CẬP NHẬT LOẠI PHÒNG THÀNH CÔNG',
                });
            },
            onError: (error: any) => {
                handleMessageError(error);
            },
        },
    );

    const onGoList = () => {
        reset({});
        navigate(`${appRoutes.ADMIN.ROOM.CATEGORY}`, { replace: true });
    };

    const onSubmit = (data: any) => {
        const body: RequestRoomTypeDTO = {
            name: data.name,
            bgColor: data?.bgColor,
            sortOrder: Number(data.sortOrder),
            price_hour: Number(data.price_hour),
            price_night: Number(data.price_night),
            price_overtime: Number(data.price_overtime),
        };
        id ? mutateUpdate(body) : mutateCreate(body);
    };

    useEffect(() => {
        if (roomTypeItem) {
            reset({
                name: roomTypeItem.data?.name,
                sortOrder: roomTypeItem.data?.sortOrder,
                bgColor: roomTypeItem?.data?.bgColor,
                price_hour: roomTypeItem?.data?.price_hour,
                price_night: roomTypeItem?.data?.price_night,
                price_overtime: roomTypeItem?.data?.price_overtime,
            });
        }
    }, [roomTypeItem, reset]);

    return (
        <Form labelCol={{ span: 6 }} onFinish={handleSubmit(onSubmit)}>
            <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                <FormItemWrap label="Tên" required validateStatus={errors?.name ? 'error' : ''}>
                    <Controller
                        name="name"
                        control={control}
                        rules={{ required: true, maxLength: 30 }}
                        render={({ field }) => <InputWrap field={field} />}
                    />
                </FormItemWrap>
                <FormItemWrap labelAlign="right" label="Màu nền" colon={false}>
                    <Controller
                        name="bgColor"
                        defaultValue={'#597ef7'}
                        control={control}
                        render={({ field }) => <ColorPicker field={field} defaultColor={'#597ef7'} />}
                    />
                </FormItemWrap>
                <FormItemWrap
                    labelAlign="right"
                    label="Giá theo giờ"
                    required
                    colon={false}
                    validateStatus={errors?.salesPrice ? 'error' : ''}
                >
                    <Controller
                        name="price_hour"
                        control={control}
                        rules={{
                            required: true,
                            pattern: pattern.inputNumber,
                        }}
                        render={({ field }) => (
                            <InputMoney
                                textAlign="right"
                                addonAfter="₫/giờ đầu"
                                placeholder="0"
                                maxLength={9}
                                field={field}
                            />
                        )}
                    />
                </FormItemWrap>

                <FormItemWrap
                    labelAlign="right"
                    label="Giá qua đêm"
                    required
                    colon={false}
                    validateStatus={errors?.salesPrice ? 'error' : ''}
                >
                    <Controller
                        name="price_night"
                        control={control}
                        rules={{
                            required: true,
                            pattern: pattern.inputNumber,
                        }}
                        render={({ field }) => (
                            <InputMoney
                                textAlign="right"
                                addonAfter="₫/đêm"
                                placeholder="0"
                                maxLength={9}
                                field={field}
                            />
                        )}
                    />
                </FormItemWrap>
                <FormItemWrap
                    labelAlign="right"
                    label="Giá quá giờ"
                    required
                    colon={false}
                    validateStatus={errors?.salesPrice ? 'error' : ''}
                >
                    <Controller
                        name="price_overtime"
                        control={control}
                        rules={{
                            required: true,
                            pattern: pattern.inputNumber,
                        }}
                        render={({ field }) => (
                            <InputMoney
                                textAlign="right"
                                addonAfter="₫/giờ"
                                placeholder="0"
                                maxLength={9}
                                field={field}
                            />
                        )}
                    />
                </FormItemWrap>
                <FormItemWrap label="Sắp xếp" required validateStatus={errors?.sortOrder ? 'error' : ''}>
                    <Controller
                        name="sortOrder"
                        control={control}
                        rules={{ required: true, pattern: pattern.inputNumber }}
                        render={({ field }) => <InputOnlyNumber maxLength={4} field={field} />}
                    />
                </FormItemWrap>

                <Row justify="end" gutter={16}>
                    <Col>
                        <ButtonWrap
                            isLoading={isLoadingCreate || isLoadingUpdate}
                            htmlType="submit"
                            text={!id ? 'THÊM MỚI' : 'CẬP NHẬT'}
                            type="primary"
                            width="140px"
                        />
                    </Col>
                </Row>
            </Col>
        </Form>
    );
};

export default memo(FormData);
